import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  AuthHeading,
  AuthPrimaryButton,
  AuthShell,
} from '../components/AuthShell';
import { AuthErrorBanner } from '../components/fieldError';
import { useLooseSearch } from '../looseSearch';
import { useAuthActions } from '../hooks/useAuthActions';
import { useAutoVerifyEmail } from '../hooks/useAutoVerifyEmail';

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * Email verification page.
 *
 * Uses the shared `AuthShell` so this screen is the same frame as /login and
 * /signup — centred column, same widths, 42px serif heading, black primary
 * action. It previously carried its own copy of the shell with a 28px sans
 * heading, a `max-w-[420px]` column and a purple button, so the flow changed
 * its visual language between signing up and confirming the address.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { userId: searchUserId } = useLooseSearch<{ userId?: string }>();
  const navigate = useNavigate();
  const {
    verifyEmail,
    resendVerification,
    cancelVerification,
    error,
    userId,
    isVerifying,
    isResending,
    emailDelivered,
    setUserIdExternal,
  } = useAuthActions();

  useEffect(() => {
    if (searchUserId && !userId) {
      setUserIdExternal(searchUserId);
    }
  }, [searchUserId, userId, setUserIdExternal]);

  // The page moves on by itself once the link has been opened, so the button
  // below is a fallback and not the only way forward. See useAutoVerifyEmail
  // for the triggers and for why none of them is a timer.
  useAutoVerifyEmail({
    check: () => verifyEmail('', { silent: true }),
    enabled: Boolean(userId),
    isBusy: isVerifying,
  });

  const activeUserId =
    userId || searchUserId || (import.meta.env.DEV ? 'preview' : undefined);

  if (!activeUserId) {
    navigate({ to: '/login', replace: true });
    return null;
  }

  return (
    <AuthShell dataName="VerifyEmail">
      {/* Identity Platform mails a LINK -> there is no code to type; the
          backend confirms email_verified on the Firebase record instead, so
          the user's only action is to open it. The page then continues on its own
          (see useAutoVerifyEmail); "I've confirmed" stays as the fallback.

          This used to be one arm of `isLinkVerification ? … : <VerifyEmailForm/>`
          — a ternary on a `const … = true` written four lines above it, so the
          other arm could never run. It held a six-box code form for a code the
          email does not contain. */}
          <AuthHeading title={t('verify.title')}>
            {emailDelivered
              ? // Short because the heading above it already says to check
                // the inbox. What is left is what to do, and that this page
                // carries on by itself afterwards.
                t('verify.subtitle')
              : undefined}
          </AuthHeading>

          {/* Announced politely and kept on screen, so a screen-reader user
              knows nothing needs pressing and the page may move on by itself. */}
          {emailDelivered && (
            <p
              role="status"
              className="w-full text-center text-[14px] text-[#3e3e3e]"
            >
              {isVerifying ? t('verify.checking') : t('verify.status_waiting')}
            </p>
          )}

          {!emailDelivered && (
            /* The account exists but nothing was sent. Telling the user to
               check an inbox that will stay empty is worse than admitting the
               failure — they would wait, retry, and conclude the product is
               broken with no way to say why.
               Left-aligned on purpose: this is a paragraph of remediation to
               read, not a heading to scan, and centring it hurts both. */
            <div
              role="alert"
              className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[14px] leading-[1.6] text-amber-900"
            >
              <p className="font-medium">{t('verify.not_sent_title')}</p>
              <p className="mt-1">
                {t('verify.not_sent_body_prefix')}{' '}
                <a className="underline" href="mailto:support@biasafe.ai">
                  support@biasafe.ai
                </a>{' '}
                {t('verify.not_sent_body_suffix')}
              </p>
            </div>
          )}

          {error ? (
            <AuthErrorBanner className="w-full">{error}</AuthErrorBanner>
          ) : null}

          <AuthPrimaryButton
            onClick={() => verifyEmail('')}
            disabled={isVerifying}
          >
            {isVerifying ? t('verify.checking') : t('verify.confirm')}
          </AuthPrimaryButton>

          <div className="flex items-center justify-center gap-4 text-[14px]">
            <button
              type="button"
              onClick={resendVerification}
              disabled={isResending}
              className="text-[#121212] font-bold hover:underline disabled:opacity-60"
            >
              {isResending ? t('verify.resending') : t('verify.resend')}
            </button>
            <span className="text-[#d4d4d8]">|</span>
            <button
              type="button"
              onClick={cancelVerification}
              className="text-[#3e3e3e] hover:underline"
            >
              {t('verify.different_address')}
            </button>
          </div>
    </AuthShell>
  );
}
